/**
 * ONBOARDING STORE
 *
 * Manages the 4-step onboarding checklist flow.
 * Persisted to localStorage so progress survives page reloads.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

// ── Types ───────────────────────────────────────────────────────────

export type OnboardingStepId =
  | "create_workspace"
  | "create_project"
  | "add_task"
  | "run_agent"
  | "review_result";

export interface OnboardingStep {
  id: OnboardingStepId;
  label: string;
  completed: boolean;
}

interface OnboardingState {
  steps: OnboardingStep[];
  isVisible: boolean;
  isCompleted: boolean;
  isDismissed: boolean;
  isMinimized: boolean;
  seenHints: Record<string, boolean>;

  // Transient UI flags (not persisted) — trigger modals from checklist clicks
  requestCreateWorkspace: boolean;
  requestAddProject: boolean;

  completeStep: (stepId: OnboardingStepId) => void;
  resetOnboarding: () => void;
  minimize: () => void;
  expand: () => void;
  dismiss: () => void;
  setRequestCreateWorkspace: (v: boolean) => void;
  setRequestAddProject: (v: boolean) => void;
  markHintSeen: (id: string) => void;
  hasSeenHint: (id: string) => boolean;
}

// ── Default steps ───────────────────────────────────────────────────

const DEFAULT_STEPS: OnboardingStep[] = [
  { id: "create_workspace", label: "Create a workspace", completed: false },
  { id: "create_project", label: "Add a project", completed: false },
  { id: "add_task", label: "Create a task", completed: false },
  { id: "run_agent", label: "Run an agent", completed: false },
  { id: "review_result", label: "Review a result", completed: false },
];

// ── Store ───────────────────────────────────────────────────────────

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      steps: DEFAULT_STEPS.map((s) => ({ ...s })),
      isVisible: true,
      isCompleted: false,
      isDismissed: false,
      isMinimized: false,
      seenHints: {},
      requestCreateWorkspace: false,
      requestAddProject: false,

      completeStep: (stepId) => {
        const { steps, isCompleted } = get();
        if (isCompleted) return;

        const updated = steps.map((s) =>
          s.id === stepId ? { ...s, completed: true } : s
        );
        const allDone = updated.every((s) => s.completed);

        set({
          steps: updated,
          isCompleted: allDone,
          isVisible: !allDone,
        });
      },

      resetOnboarding: () =>
        set({
          steps: DEFAULT_STEPS.map((s) => ({ ...s })),
          isCompleted: false,
          isDismissed: false,
          isMinimized: false,
          isVisible: true,
          seenHints: {},
        }),

      minimize: () => set({ isMinimized: true }),
      expand: () => set({ isMinimized: false }),
      dismiss: () => set({ isDismissed: true, isVisible: false }),
      setRequestCreateWorkspace: (v) => set({ requestCreateWorkspace: v }),
      setRequestAddProject: (v) => set({ requestAddProject: v }),
      markHintSeen: (id) => set((s) => ({ seenHints: { ...s.seenHints, [id]: true } })),
      hasSeenHint: (id) => !!get().seenHints[id],
    }),
    {
      name: "factory-onboarding",
      version: 1,
      partialize: (state) => ({
        steps: state.steps,
        isCompleted: state.isCompleted,
        isDismissed: state.isDismissed,
        seenHints: state.seenHints,
      }),
    }
  )
);

// ── Helper hook ─────────────────────────────────────────────────────

/** Returns true when the checklist should be rendered. */
export function useShouldShowChecklist(): boolean {
  const { isVisible, isDismissed, isCompleted } = useOnboardingStore();
  return isVisible && !isDismissed && !isCompleted;
}

/** Returns true when the given step is the current active (first uncompleted) step
 *  AND the checklist hasn't been completed or dismissed. */
export function useIsActiveStep(stepId: OnboardingStepId): boolean {
  return useOnboardingStore((s) => {
    if (s.isCompleted || s.isDismissed) return false;
    const active = s.steps.find((step) => !step.completed);
    return active?.id === stepId;
  });
}

// ── Existing-user detection ─────────────────────────────────────────

interface TaskLike {
  workflow_status?: string | null;
}

/**
 * Auto-complete onboarding steps based on existing world state.
 * Call imperatively from a useEffect, passing worldStore data as arguments.
 */
export function initOnboardingFromWorldState(
  projects: unknown[],
  tasks: TaskLike[],
  workspaceCount?: number,
): void {
  const state = useOnboardingStore.getState();
  if (state.isCompleted || state.isDismissed) return;

  if (workspaceCount && workspaceCount > 0) {
    state.completeStep("create_workspace");
  }
  if (projects.length > 0) {
    state.completeStep("create_project");
  }
  if (tasks.length > 0) {
    state.completeStep("add_task");
  }
  if (
    tasks.some(
      (t) =>
        t.workflow_status === "completed" || t.workflow_status === "running"
    )
  ) {
    state.completeStep("run_agent");
  }
}
